import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { fetchCharterInventory } from '~/services/CharterService';
import * as TimeService from '~/services/TimeService';

import { CharterInventoryFlight, CharterInventoryFilters as ContractCharterInventoryFilters } from './charter.types';
import { compareByDate } from './charterInventoryUtils';

export type CharterInventoryFilters = ContractCharterInventoryFilters & {
  startDate?: string | null;
  endDate?: string | null;
};

export type PairedFlightRow = {
  id: string;
  stayDuration: number;
  departing: CharterInventoryFlight;
  returning?: CharterInventoryFlight;
};

type UseCharterInventoryResult = {
  flights: Array<CharterInventoryFlight>;
  pairedRows: Array<PairedFlightRow>;
  canDownload: boolean;
  noResults: boolean;
  queryError: Error | null;
  isFetching: boolean;
};

export const useCharterInventory = (filters: CharterInventoryFilters | null): UseCharterInventoryResult => {
  const {
    data: flights = [],
    isFetching,
    error,
  } = useQuery<Array<CharterInventoryFlight>>({
    queryKey: ['charterInventory', filters],
    queryFn: () => {
      if (!filters) {
        return Promise.resolve([]);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { startDate: _startDate, endDate: _endDate, ...apiFilters } = filters;
      return fetchCharterInventory(apiFilters);
    },
    enabled: Boolean(filters),
    staleTime: 0,
  });

  const filteredFlights = useMemo(() => {
    if (!filters) {
      return [] as Array<CharterInventoryFlight>;
    }

    return flights
      .map((flight) => ({
        ...flight,
        fareInventory: (flight.fareInventory ?? []).filter((fare) => filters.cabinClasses.includes(fare.cabinClass)),
      }))
      .filter((flight) => flight.fareInventory.length > 0);
  }, [flights, filters]);

  const departingFlights = useMemo(() => {
    if (!filters) {
      return [] as Array<CharterInventoryFlight>;
    }

    return filteredFlights
      .filter((flight) => {
        if (flight.depAirport !== filters.departureAirport || flight.arrAirport !== filters.arrivalAirport) {
          return false;
        }

        const depDate = TimeService.toDayJs(flight.depDate);
        if (!depDate.isValid()) {
          return false;
        }

        if (filters.startDate && depDate.isBefore(TimeService.toDayJs(filters.startDate), 'day')) {
          return false;
        }

        if (filters.endDate && depDate.isAfter(TimeService.toDayJs(filters.endDate), 'day')) {
          return false;
        }

        const departureMonth = depDate.month() + 1;
        return filters.departingMonths.includes(departureMonth);
      })
      .sort((a, b) => compareByDate(a.depDate, b.depDate));
  }, [filteredFlights, filters]);

  const returnFlights = useMemo(() => {
    if (!filters) {
      return [] as Array<CharterInventoryFlight>;
    }

    return filteredFlights
      .filter(
        (flight) =>
          flight.depAirport === filters.returnDepartureAirport && flight.arrAirport === filters.returnArrivalAirport,
      )
      .sort((a, b) => compareByDate(a.depDate, b.depDate));
  }, [filteredFlights, filters]);

  const pairedRows = useMemo(() => {
    if (!filters) {
      return [] as Array<PairedFlightRow>;
    }

    const rows: Array<PairedFlightRow> = [];

    departingFlights.forEach((departing) => {
      filters.stayDurations.forEach((stayDuration) => {
        const arrivalDate = TimeService.toDayJs(departing.arrDate);
        if (!arrivalDate.isValid()) {
          return;
        }

        const expectedReturnDate = arrivalDate.add(stayDuration, 'day');

        // Find matching return flight
        const returning = returnFlights.find((candidate) => {
          if (candidate.aircraftId !== departing.aircraftId) {
            return false;
          }
          return TimeService.toDayJs(candidate.depDate).isSame(expectedReturnDate, 'day');
        });

        rows.push({
          id: `${departing.flightInstanceId}-${stayDuration}`,
          stayDuration,
          departing,
          returning,
        });
      });
    });

    return rows.sort((a, b) => {
      const dateCompare = compareByDate(a.departing.depDate, b.departing.depDate);
      if (dateCompare !== 0) {
        return dateCompare;
      }
      return a.stayDuration - b.stayDuration;
    });
  }, [departingFlights, returnFlights, filters]);

  const canDownload = pairedRows.length > 0;
  const queryError = error instanceof Error ? error : null;
  const noResults = Boolean(filters) && !isFetching && !canDownload;

  return {
    flights,
    pairedRows,
    canDownload,
    noResults,
    queryError,
    isFetching,
  };
};
